import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BrandLogo from '../components/BrandLogo';

// Platform sign-in. Email entered -> CHOICE: email code (primary) or
// password (break-glass fallback, platform accounts only). Unadvertised.
const AdminLogin = () => {
  const [step, setStep] = useState('email'); // email | choice | code | password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const { requestCode, verifyCode, login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated()) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const goChoice = (e) => {
    e?.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Enter your email');
      return;
    }
    setStep('choice');
  };

  const sendCode = async () => {
    setError('');
    setInfo('');
    setLoading(true);
    const r = await requestCode(email.trim().toLowerCase());
    setLoading(false);
    if (r.success) {
      setStep('code');
      setInfo('If that account exists, a sign-in code has been emailed.');
      setCooldown(60);
    } else {
      setError(r.error);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError('');
    if (code.trim().length !== 6) {
      setError('Enter the 6-digit code');
      return;
    }
    setLoading(true);
    const r = await verifyCode(email.trim().toLowerCase(), code.trim());
    setLoading(false);
    if (r.success) navigate('/');
    else setError(r.error);
  };

  const doPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!password) {
      setError('Enter your password');
      return;
    }
    setLoading(true);
    const r = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (r.success) navigate('/');
    else setError(r.error || 'Sign in failed');
  };

  const inputCls =
    'w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all';

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f2d5e]">
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <BrandLogo className="mb-4" />
          <h2 className="text-xl font-semibold text-white">Platform</h2>
          <p className="text-[13px] text-slate-300 mt-1">Staff sign in</p>
        </div>

        <div className="rounded-xl bg-white p-6">
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 border border-red-200/80 px-3 py-2.5 text-[13px] text-red-600">{error}</div>
          )}
          {info && (
            <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200/80 px-3 py-2.5 text-[13px] text-blue-700">{info}</div>
          )}

          {step === 'email' && (
            <form onSubmit={goChoice} className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Email</label>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                  placeholder="admin@mysms360.com"
                  disabled={loading}
                />
              </div>
              <button type="submit" className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors">
                Continue
              </button>
            </form>
          )}

          {step === 'choice' && (
            <div className="space-y-3">
              <p className="text-[13px] text-slate-600">Signing in as <span className="font-medium">{email}</span></p>
              <button
                onClick={sendCode}
                disabled={loading}
                className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Sending…' : 'Email me a sign-in code'}
              </button>
              <button
                onClick={() => { setError(''); setStep('password'); }}
                className="w-full py-2 px-4 rounded-lg bg-blue-600 text-[13px] font-medium text-white hover:bg-blue-700 transition-colors"
              >
                Use password instead
              </button>
              <button
                onClick={() => { setError(''); setStep('email'); }}
                className="w-full text-[12px] text-slate-400 hover:text-slate-600"
              >
                ← Use a different email
              </button>
            </div>
          )}

          {step === 'code' && (
            <form onSubmit={verify} className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">6-digit code</label>
                <input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className={`${inputCls} text-center tracking-[0.5em] text-lg font-mono`}
                  placeholder="000000"
                  disabled={loading}
                  autoFocus
                />
              </div>
              <button type="submit" disabled={loading} className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
                {loading ? 'Verifying…' : 'Sign in'}
              </button>
              <button type="button" disabled={cooldown > 0 || loading} onClick={sendCode} className="w-full text-[13px] text-slate-500 hover:text-slate-700 disabled:opacity-50">
                {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
              </button>
            </form>
          )}

          {step === 'password' && (
            <form onSubmit={doPassword} className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Password</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                  placeholder="Break-glass password"
                  disabled={loading}
                  autoFocus
                />
              </div>
              <button type="submit" disabled={loading} className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
              <button type="button" onClick={() => { setError(''); setStep('choice'); }} className="w-full text-[12px] text-slate-400 hover:text-slate-600">
                ← Back
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
